import * as bulmaToast from 'bulma-toast'
import { AUTH_PAGES_SWITCH } from '@/utils/authPages.js'
import { SUBSCRIBE_FLOW_SWITCH, goToSubscribe } from '@/utils/subscribeFlow.js'

// mutation types
const SET_LOADING_INIT_AUTHENTICATED = 'SET_LOADING_INIT_AUTHENTICATED'
const SET_PREVIEW_DOWNLOAD_URL = 'SET_PREVIEW_DOWNLOAD_URL'
const SET_SHOW_PREVIEW_DOWNLOAD_MODAL = 'SET_SHOW_PREVIEW_DOWNLOAD_MODAL'
const SET_TRACK_PROMPT = 'SET_TRACK_PROMPT'

// initial state
const state = {
  loadingInitAuthenticated: false,
  previewDownloadUrl: null,
  showPreviewDownloadModal: false,
  // The track someone tried to use and why ({ reason, track }), while
  // TheTrackPromptModal asks them to create an account or choose a plan first.
  trackPrompt: null
}

// getters
const getters = {
  loadingInitAuthenticated: (state) => state.loadingInitAuthenticated,

  previewDownloadUrl: (state) => state.previewDownloadUrl,

  showPreviewDownloadModal: (state) => state.showPreviewDownloadModal,

  trackPrompt: (state) => state.trackPrompt
}

// actions
const actions = {
  initAuthenticated: (context) => {
    context.commit(SET_LOADING_INIT_AUTHENTICATED, true)

    return Promise.all([
      context.dispatch('user/setMe', null, { root: true })
        .then(() => {
          const team = Object.assign({}, context.rootGetters['user/currentTeam'])
          if (team.hasOwnProperty('current_subscription') && team.current_subscription) {
            return context.dispatch('subscriptions/fetchCurrentSubscription', null, { root: true })
          }
        }),
      context.dispatch('favouriteTracks/fetchFavouriteTracksLite', null, { root: true }),
      context.dispatch('licenses/fetchLicensesLite', null, { root: true })
    ]).finally(() => {
      context.commit(SET_LOADING_INIT_AUTHENTICATED, false)
    })
  },

  initPublic: (context) => {
    // Get products and plans so users can buy 'em!
    context.dispatch('cart/fetchProducts', null, { root: true })
    context.dispatch('subscriptions/fetchPlans', null, { root: true })
  },

  setPreviewDownloadUrl: (context, payload) => {
    context.commit(SET_PREVIEW_DOWNLOAD_URL, payload)
  },

  showPreviewDownloadModal: (context) => {
    context.commit(SET_SHOW_PREVIEW_DOWNLOAD_MODAL, true)
  },

  hidePreviewDownloadModal: (context) => {
    context.commit(SET_SHOW_PREVIEW_DOWNLOAD_MODAL, false)
  },

  showTrackPrompt: (context, prompt) => {
    context.commit(SET_TRACK_PROMPT, prompt)
  },

  hideTrackPrompt: (context) => {
    context.commit(SET_TRACK_PROMPT, null)
  },

  // prompt ({ reason, track }) is passed on to showSignupModal when it was a track
  // action that needed an account.
  startSignupWorkflow: (context, prompt) => {
    const isLoggedIn = context.rootGetters['auth/isLoggedIn']
    // Signing up no longer starts with choosing a plan, so go straight to Create account.
    if (!isLoggedIn && context.rootGetters['waffle/isFeatureEnabled'](AUTH_PAGES_SWITCH)) {
      return context.dispatch('user/showSignupModal', prompt, { root: true })
    }
    if (isLoggedIn && context.rootGetters['waffle/isFeatureEnabled'](SUBSCRIBE_FLOW_SWITCH)) {
      // Imported here because the router imports the store.
      return import('@/router.js').then(({ default: router }) => goToSubscribe(router, context.rootGetters['user/currentTeam']))
    }
    if (isLoggedIn) {
      context.dispatch('subscriptions/showPlanSelectionModal', null, { root: true })
    } else {
      if (context.rootGetters['subscriptions/selectedPlan']) {
        context.dispatch('user/showSignupModal', null, { root: true })
      } else {
        context.dispatch('subscriptions/showPlanSelectionModal', null, { root: true })
      }
    }
  },

  showToastMessage: (context, payload) => {
    bulmaToast.toast(payload)
  }
}

// mutations
const mutations = {
  [SET_LOADING_INIT_AUTHENTICATED]: (state, value) => {
    state.loadingInitAuthenticated = value
  },

  [SET_PREVIEW_DOWNLOAD_URL]: (state, payload) => {
    state.previewDownloadUrl = payload
  },

  [SET_SHOW_PREVIEW_DOWNLOAD_MODAL]: (state, payload) => {
    state.showPreviewDownloadModal = payload
  },

  [SET_TRACK_PROMPT]: (state, payload) => {
    state.trackPrompt = payload
  }
}

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations
}
